import { HttpErrorResponse } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ApiError } from './api-error';

// Fase 2 (design-handoff/planificacion_fase_2.md) wraps every auth response —
// success and error alike — in this envelope. It's a different shape from the
// rest of the app's placeholder `{data:T}`/`{error:{code,fieldErrors}}`
// convention in api-error.ts (no top-level `status` there, and `fields` here
// is a flat `Record<string,string>` rather than `fieldErrors`'s string
// arrays), so it gets its own small helper instead of bending the shared one
// two ways.
export interface ApiEnvelope<T> {
  status: 'success' | 'error';
  data: T | null;
  message: string | null;
  error: { code: string; fields?: Record<string, string> } | null;
}

export class AuthApiError<TCode extends string = string> extends ApiError<TCode> {
  constructor(code: TCode, status: number, message: string, readonly fields?: Record<string, string>) {
    super(code, status, message);
  }
}

export function unwrapEnvelope<T>() {
  return (source: Observable<ApiEnvelope<T>>): Observable<T> =>
    source.pipe(
      map((envelope) => {
        if (!envelope.data) {
          throw new Error('Expected a successful envelope with data.');
        }
        return envelope.data;
      }),
    );
}

// A non-2xx or a JSON-parse failure both land here as `response.error`, but a
// parse failure puts the raw SyntaxError/ProgressEvent there instead of our
// envelope — and SyntaxError happens to have its own `.message`, which would
// otherwise leak a raw JS parser error ("JSON.parse: unexpected character...")
// straight into the UI. Only trust the body when it actually looks like our
// envelope.
function isApiEnvelope(value: unknown): value is ApiEnvelope<unknown> {
  return typeof value === 'object' && value !== null && !(value instanceof Error) && 'status' in value;
}

export function toAuthApiError<TCode extends string>(
  response: HttpErrorResponse,
  fallbackCode: TCode,
): AuthApiError<TCode> {
  const body = isApiEnvelope(response.error) ? response.error : undefined;
  const code = (body?.error?.code as TCode | undefined) ?? fallbackCode;
  const message = body?.message ?? 'Ocurrió un error inesperado.';
  return new AuthApiError(code, response.status, message, body?.error?.fields);
}
